// src/routes/Home/HomeComponents/KPISummaryCard.js

import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Grid, useTheme, CircularProgress, alpha } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import axios from '../../../utils/axiosConfig';
import { designTokens, liquidGlassCardDark } from '../../../theme/designTokens';

// カウントアップアニメーション用のカスタムフック
const useCountUp = (end, duration = 1000) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      setCount(Math.floor(progress * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return count;
};

// KPI個別カードコンポーネント（プライマリ - 大きい表示）
function KPIItemPrimary({ icon: Icon, label, value, color, trend, loading, onClick }) {
  const theme = useTheme();
  const isNumeric = typeof value === 'number' && !isNaN(value);
  const displayValue = useCountUp(isNumeric ? value : 0, 800);

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 3,
        borderRadius: 2,
        ...liquidGlassCardDark(theme),
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            backgroundColor: alpha(color, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 24, color }} />
        </Box>

        {trend !== undefined && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: trend >= 0 ? alpha(designTokens.statusColors.high.primary, 0.1) : alpha(designTokens.statusColors.low.primary, 0.1),
            }}
          >
            {trend >= 0 ? (
              <TrendingUpIcon sx={{ fontSize: 16, color: designTokens.statusColors.high.primary }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 16, color: designTokens.statusColors.low.primary }} />
            )}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: trend >= 0 ? designTokens.statusColors.high.primary : designTokens.statusColors.low.primary,
              }}
            >
              {Math.abs(trend)}%
            </Typography>
          </Box>
        )}
      </Box>

      <Box>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            mb: 1,
          }}
        >
          {label}
        </Typography>

        {loading ? (
          <CircularProgress size={32} sx={{ color }} />
        ) : (
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              color: color,
              lineHeight: 1,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            {isNumeric ? displayValue : value}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// KPI個別カードコンポーネント（セカンダリ - 小さい表示）
function KPIItemSecondary({ icon: Icon, label, value, color, trend, loading, onClick }) {
  const theme = useTheme();
  // valueが数値の場合のみカウントアップアニメーションを使用
  const isNumeric = typeof value === 'number' && !isNaN(value);
  const displayValue = useCountUp(isNumeric ? value : 0, 800);

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 2,
        ...liquidGlassCardDark(theme),
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            backgroundColor: alpha(color, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 20, color }} />
        </Box>

        {trend !== undefined && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              backgroundColor: trend >= 0 ? alpha(designTokens.statusColors.high.primary, 0.1) : alpha(designTokens.statusColors.low.primary, 0.1),
            }}
          >
            {trend >= 0 ? (
              <TrendingUpIcon sx={{ fontSize: 14, color: designTokens.statusColors.high.primary }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 14, color: designTokens.statusColors.low.primary }} />
            )}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                color: trend >= 0 ? designTokens.statusColors.high.primary : designTokens.statusColors.low.primary,
              }}
            >
              {Math.abs(trend)}%
            </Typography>
          </Box>
        )}
      </Box>

      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      {loading ? (
        <CircularProgress size={24} sx={{ color }} />
      ) : (
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: color,
            lineHeight: 1,
          }}
        >
          {isNumeric ? displayValue : value}
        </Typography>
      )}
    </Box>
  );
}

function KPISummaryCard() {
  const BACKEND_URL = process.env.REACT_APP_API_URL;
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState({
    totalApplicants: 0,
    hiredCount: 0,
    inProgressInterviews: 0,
    daysToNextDeadline: null,
  });

  useEffect(() => {
    const fetchKPIData = async () => {
      try {
        setLoading(true);

        // 並列でデータ取得
        const [interviewStatsRes, deadlinesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/interview/progress-stats`, {
            withCredentials: true,
          }).catch(() => ({ data: null })),

          axios.get(`${BACKEND_URL}/deadlines/`, {
            withCredentials: true,
          }).catch(() => ({ data: null })),
        ]);

        // 面接統計データ
        const interviewData = interviewStatsRes.data;
        const totalApplicants = interviewData?.total_candidates || 0;
        const hiredCount = interviewData?.decision_stats?.hired || 0;

        // 進行中の面接数を計算（total - hired - rejected）
        const rejectedCount = interviewData?.decision_stats?.rejected || 0;
        const inProgressInterviews = Math.max(0, totalApplicants - hiredCount - rejectedCount);

        // 締切データから次の締切を計算
        let daysToNextDeadline = null;
        if (deadlinesRes.data) {
          const deadlineData = Array.isArray(deadlinesRes.data) ? deadlinesRes.data[0] : deadlinesRes.data;
          const now = new Date();
          const deadlines = [];

          // deadlineData が存在する場合のみ処理
          if (deadlineData) {
            if (deadlineData.es_deadline) {
              deadlines.push(new Date(deadlineData.es_deadline));
            }
            if (deadlineData.naitei_deadline) {
              deadlines.push(new Date(deadlineData.naitei_deadline));
            }
            if (deadlineData.additional_deadlines) {
              deadlineData.additional_deadlines.forEach(d => {
                if (d.date) deadlines.push(new Date(d.date));
              });
            }

            // 未来の締切のみをフィルタ
            const futureDeadlines = deadlines.filter(d => d > now);

            if (futureDeadlines.length > 0) {
              // 最も近い締切を取得
              const nextDeadline = new Date(Math.min(...futureDeadlines));
              const diffTime = nextDeadline - now;
              daysToNextDeadline = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }
          }
        }

        setKpiData({
          totalApplicants,
          hiredCount,
          inProgressInterviews,
          daysToNextDeadline,
        });
      } catch (error) {
        console.error('Failed to fetch KPI data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchKPIData();
  }, [BACKEND_URL]);

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* ヘッダー */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 0.5,
            }}
          >
            採用活動サマリー
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: 'long' })}
          </Typography>
        </Box>
      </Box>

      {/* 階層化KPI Grid - プライマリ(大) + セカンダリ(小) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        {/* プライマリKPI - 総応募者（大きく表示） */}
        <KPIItemPrimary
          icon={PeopleAltIcon}
          label="総応募者"
          value={kpiData.totalApplicants}
          color={theme.palette.info.main}
          loading={loading}
        />

        {/* セカンダリKPIグリッド */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gridTemplateRows: 'repeat(2, 1fr)',
            gap: 2,
          }}
        >
          <KPIItemSecondary
            icon={CheckCircleIcon}
            label="内定者数"
            value={kpiData.hiredCount}
            color={designTokens.statusColors.high.primary}
            trend={kpiData.hiredCount > 0 ? 12 : undefined}
            loading={loading}
          />

          <KPIItemSecondary
            icon={WorkIcon}
            label="進行中"
            value={kpiData.inProgressInterviews}
            color={designTokens.statusColors.medium.primary}
            loading={loading}
          />

          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
            <KPIItemSecondary
              icon={AccessTimeIcon}
              label="次の締切まで"
              value={kpiData.daysToNextDeadline !== null ? `${kpiData.daysToNextDeadline}日` : '-'}
              color={
                kpiData.daysToNextDeadline === null
                  ? designTokens.colors.neutral[400]
                  : kpiData.daysToNextDeadline <= 7
                    ? designTokens.statusColors.low.primary
                    : kpiData.daysToNextDeadline <= 30
                      ? designTokens.statusColors.medium.primary
                      : designTokens.statusColors.high.primary
              }
              loading={loading}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default KPISummaryCard;
